import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Toggle } from '../../components/ui/Toggle';
import { Badge } from '../../components/ui/Badge';
import { Settings, Database, Mail, Shield, Zap, Bell, Globe, Save, AlertCircle } from 'lucide-react';
export function SystemSettingsPage() {
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Settings state
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    newBuilderSignups: true,
    emailNotifications: true,
    telemetryTracking: true,
    autoBackups: true,
    sessionTimeout: '30',
    maxFileSize: '10',
    apiRateLimit: '1000',
    defaultProjectQuota: '10',
    emailProvider: 'sendgrid',
    storageProvider: 'aws-s3'
  });
  const handleToggle = (key: string, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setUnsavedChanges(true);
  };
  const handleInputChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setUnsavedChanges(true);
  };
  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setUnsavedChanges(false);
    }, 1000);
  };
  return <div className="space-y-6 max-w-5xl mx-auto" data-id="element-2944">
      <div className="flex items-center justify-between" data-id="element-2945">
        <div data-id="element-2946">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-2947">System Settings</h1>
          <p className="text-slate-500" data-id="element-2948">
            Configure platform-wide settings and preferences
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-2949">
          {unsavedChanges && <span className="text-sm text-amber-600 font-medium flex items-center bg-amber-50 px-3 py-1 rounded-full" data-id="element-2950">
              <AlertCircle className="h-4 w-4 mr-2" data-id="element-2951" />
              Unsaved Changes
            </span>}
          <Button onClick={handleSave} isLoading={isSaving} disabled={!unsavedChanges} leftIcon={<Save className="h-4 w-4" data-id="element-2953" />} data-id="element-2952">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Platform Settings */}
      <Card data-id="element-2954">
        <CardHeader data-id="element-2955">
          <div className="flex items-center gap-2" data-id="element-2956">
            <Settings className="h-5 w-5 text-slate-600" data-id="element-2957" />
            <CardTitle data-id="element-2958">Platform Settings</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-2959">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2960">
            <div className="flex-1" data-id="element-2961">
              <p className="font-medium text-slate-900" data-id="element-2962">Maintenance Mode</p>
              <p className="text-sm text-slate-500" data-id="element-2963">
                Temporarily disable platform access for all users
              </p>
            </div>
            <Toggle checked={settings.maintenanceMode} onChange={checked => handleToggle('maintenanceMode', checked)} data-id="element-2964" />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2965">
            <div className="flex-1" data-id="element-2966">
              <p className="font-medium text-slate-900" data-id="element-2967">New Builder Signups</p>
              <p className="text-sm text-slate-500" data-id="element-2968">
                Allow new builder companies to sign up
              </p>
            </div>
            <Toggle checked={settings.newBuilderSignups} onChange={checked => handleToggle('newBuilderSignups', checked)} data-id="element-2969" />
          </div>

          <div className="flex items-center justify-between py-3" data-id="element-2970">
            <div className="flex-1" data-id="element-2971">
              <p className="font-medium text-slate-900" data-id="element-2972">Session Timeout</p>
              <p className="text-sm text-slate-500" data-id="element-2973">
                Automatic logout after inactivity (minutes)
              </p>
            </div>
            <input type="number" value={settings.sessionTimeout} onChange={e => handleInputChange('sessionTimeout', e.target.value)} className="w-24 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-2974" />
          </div>
        </CardContent>
      </Card>

      {/* Data & Storage */}
      <Card data-id="element-2975">
        <CardHeader data-id="element-2976">
          <div className="flex items-center gap-2" data-id="element-2977">
            <Database className="h-5 w-5 text-slate-600" data-id="element-2978" />
            <CardTitle data-id="element-2979">Data & Storage</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-2980">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2981">
            <div className="flex-1" data-id="element-2982">
              <p className="font-medium text-slate-900" data-id="element-2983">Automatic Backups</p>
              <p className="text-sm text-slate-500" data-id="element-2984">
                Daily automated database backups
              </p>
            </div>
            <Toggle checked={settings.autoBackups} onChange={checked => handleToggle('autoBackups', checked)} data-id="element-2985" />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-2986">
            <div className="flex-1" data-id="element-2987">
              <p className="font-medium text-slate-900" data-id="element-2988">Max File Upload Size</p>
              <p className="text-sm text-slate-500" data-id="element-2989">Maximum file size in MB</p>
            </div>
            <input type="number" value={settings.maxFileSize} onChange={e => handleInputChange('maxFileSize', e.target.value)} className="w-24 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-2990" />
          </div>

          <div className="flex items-center justify-between py-3" data-id="element-2991">
            <div className="flex-1" data-id="element-2992">
              <p className="font-medium text-slate-900" data-id="element-2993">Storage Provider</p>
              <p className="text-sm text-slate-500" data-id="element-2994">Cloud storage service</p>
            </div>
            <select value={settings.storageProvider} onChange={e => handleInputChange('storageProvider', e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-2995">
              <option value="aws-s3" data-id="element-2996">AWS S3</option>
              <option value="google-cloud" data-id="element-2997">Google Cloud Storage</option>
              <option value="azure" data-id="element-2998">Azure Blob Storage</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Email & Notifications */}
      <Card data-id="element-2999">
        <CardHeader data-id="element-3000">
          <div className="flex items-center gap-2" data-id="element-3001">
            <Mail className="h-5 w-5 text-slate-600" data-id="element-3002" />
            <CardTitle data-id="element-3003">Email & Notifications</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-3004">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-3005">
            <div className="flex-1" data-id="element-3006">
              <p className="font-medium text-slate-900" data-id="element-3007">Email Notifications</p>
              <p className="text-sm text-slate-500" data-id="element-3008">
                Send system notifications via email
              </p>
            </div>
            <Toggle checked={settings.emailNotifications} onChange={checked => handleToggle('emailNotifications', checked)} data-id="element-3009" />
          </div>

          <div className="flex items-center justify-between py-3" data-id="element-3010">
            <div className="flex-1" data-id="element-3011">
              <p className="font-medium text-slate-900" data-id="element-3012">Email Provider</p>
              <p className="text-sm text-slate-500" data-id="element-3013">Email delivery service</p>
            </div>
            <select value={settings.emailProvider} onChange={e => handleInputChange('emailProvider', e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-3014">
              <option value="sendgrid" data-id="element-3015">SendGrid</option>
              <option value="mailgun" data-id="element-3016">Mailgun</option>
              <option value="aws-ses" data-id="element-3017">AWS SES</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Security & API */}
      <Card data-id="element-3018">
        <CardHeader data-id="element-3019">
          <div className="flex items-center gap-2" data-id="element-3020">
            <Shield className="h-5 w-5 text-slate-600" data-id="element-3021" />
            <CardTitle data-id="element-3022">Security & API</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-3023">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-3024">
            <div className="flex-1" data-id="element-3025">
              <p className="font-medium text-slate-900" data-id="element-3026">API Rate Limit</p>
              <p className="text-sm text-slate-500" data-id="element-3027">
                Requests per hour per API key
              </p>
            </div>
            <input type="number" value={settings.apiRateLimit} onChange={e => handleInputChange('apiRateLimit', e.target.value)} className="w-32 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-3028" />
          </div>

          <div className="flex items-center justify-between py-3" data-id="element-3029">
            <div className="flex-1" data-id="element-3030">
              <p className="font-medium text-slate-900" data-id="element-3031">
                Default Project Quota
              </p>
              <p className="text-sm text-slate-500" data-id="element-3032">
                Max projects per new builder
              </p>
            </div>
            <input type="number" value={settings.defaultProjectQuota} onChange={e => handleInputChange('defaultProjectQuota', e.target.value)} className="w-24 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-3033" />
          </div>
        </CardContent>
      </Card>

      {/* Analytics & Telemetry */}
      <Card data-id="element-3034">
        <CardHeader data-id="element-3035">
          <div className="flex items-center gap-2" data-id="element-3036">
            <Zap className="h-5 w-5 text-slate-600" data-id="element-3037" />
            <CardTitle data-id="element-3038">Analytics & Telemetry</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-3039">
          <div className="flex items-center justify-between py-3" data-id="element-3040">
            <div className="flex-1" data-id="element-3041">
              <p className="font-medium text-slate-900" data-id="element-3042">Telemetry Tracking</p>
              <p className="text-sm text-slate-500" data-id="element-3043">
                Collect usage analytics and event data
              </p>
            </div>
            <Toggle checked={settings.telemetryTracking} onChange={checked => handleToggle('telemetryTracking', checked)} data-id="element-3044" />
          </div>
        </CardContent>
      </Card>
    </div>;
}